<script setup>
import { useLayout } from '@/layout/composables/layout';
import { computed, ref, watch } from 'vue';
import AppFooter from './AppFooter.vue';
import Toast from 'primevue/toast';
import Header from './Header.vue';
import ProfileModal from './ProfileModal.vue';
import AppLauncher from './AppLauncher.vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserProfileStore } from '@/stores/userProfileStore';

const router = useRouter();
const route = useRoute();
const showProfileModal = ref(false);
const showAppLauncher = ref(false);

const navigationOptions = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'canaries', label: 'Canaries' }
];

const activeTab = computed(() => {
    if (route.path.startsWith('/canaries')) return 'canaries';
    return 'dashboard';
});

const handleTabChange = (tabId) => {
    if (tabId === 'dashboard') router.push('/');
    else if (tabId === 'canaries') router.push('/canaries');
};

const { layoutConfig, layoutState, isSidebarActive } = useLayout();

const containerClass = computed(() => ({
    'layout-overlay': layoutConfig.menuMode === 'overlay',
    'layout-static': layoutConfig.menuMode === 'static',
    'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
    'layout-overlay-active': layoutState.overlayMenuActive,
    'layout-mobile-active': layoutState.staticMenuMobileActive
}));

// Outside click handlers (same as before)
const outsideClickListener = ref(null);

watch(isSidebarActive, (newVal) => {
    if (newVal) bindOutsideClickListener();
    else unbindOutsideClickListener();
});

function bindOutsideClickListener() {
    if (!outsideClickListener.value) {
        outsideClickListener.value = (event) => {
            if (isOutsideClicked(event)) {
                layoutState.overlayMenuActive = false;
                layoutState.staticMenuMobileActive = false;
                layoutState.menuHoverActive = false;
            }
        };
        document.addEventListener('click', outsideClickListener.value);
    }
}

function unbindOutsideClickListener() {
    if (outsideClickListener.value) {
        document.removeEventListener('click', outsideClickListener.value);
        outsideClickListener.value = null;
    }
}

function isOutsideClicked(event) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button');
    return !(sidebarEl?.contains(event.target) || topbarEl?.contains(event.target));
}

const toggleProfileModal = () => {
    showProfileModal.value = true;
};

const navigateHome = () => {
    router.push('/');
};

const profileStore = useUserProfileStore();

const handleLogout = () => {
    // Clear all localStorage
    localStorage.clear();

    // Refresh the app
    window.location.reload();
};
</script>

<template>
    <!-- Move Header outside layout-wrapper for full-width background -->
    <Header
        app-name="Architecture Dashboard"
        :navigation-options="navigationOptions"
        :active-tab="activeTab"
        :user-avatar="profileStore.pictureUrl"
        @navigate-home="navigateHome"
        @change-tab="handleTabChange"
        @toggle-app-launcher="showAppLauncher = true"
        @toggle-mobile-menu="showProfileModal = true"
        @toggle-profile="toggleProfileModal"
        @logout="handleLogout"
    />

    <div class="layout-wrapper" :class="containerClass">
        <div class="layout-main-container">
            <div class="layout-main">
                <router-view />
            </div>
            <app-footer />
        </div>
        <div class="layout-mask animate-fadein"></div>
    </div>
    <AppLauncher v-model="showAppLauncher" />
    <ProfileModal v-model="showProfileModal" />
    <Toast position="bottom-center" class="!bg-transparent !rounded-[16px] !border-none">
        <template #container="{ message, closeCallback }">
            <div
                :class="[
                    'flex flex-row items-center justify-between flex-auto p-[16px] !rounded-[16px] min-h-[70px] h-min md:h-[70px]',
                    {
                        'bg-[#139136]': message.severity === 'success' || !message.severity,
                        'bg-[#E0011C]': message.severity === 'error',
                        'bg-[#DDE1E6]': message.severity === 'info'
                    }
                ]"
            >
                <div class="flex flex-row items-center justify-start flex-auto">
                    <i v-if="message.severity === 'info'" class="fa-solid fa-circle-info text-[22px] text-black/75 flex-shrink-0"></i>
                    <i v-else :class="['fa-solid', message.severity === 'error' ? 'fa-times-circle' : 'fa-check-circle', 'text-[22px]', 'text-white/75', 'flex-shrink-0']"></i>
                    <div :class="['body-large ml-[16px]', message.severity === 'info' ? 'text-black' : 'text-white']">{{ message.summary }}</div>
                </div>

                <Button variant="text" severity="secondary" icon="pi pi-times" :class="['!bg-transparent !border-none', message.severity === 'info' ? '!text-black/75' : '!text-white/75']" @click="closeCallback" />
            </div>
        </template>
    </Toast>
</template>
